import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Field, Loading, Modal, Notice } from './ui.js';

interface SchoolDetail {
  id: string;
  name: string;
  principal_name: string | null;
  deputy_name: string | null;
  display_token: string | null;
}

const MAX_LOGO_BYTES = 300 * 1024;

/**
 * The hall screen of one school: who it names, and the public link that opens
 * it. The link carries a secret token, so replacing it retires the old one.
 */
export function SchoolDisplayDialog({
  schoolId,
  schoolName,
  onClose,
}: {
  schoolId: string;
  schoolName: string;
  onClose: () => void;
}): JSX.Element {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [principal, setPrincipal] = useState('');
  const [deputy, setDeputy] = useState('');
  const [note, setNote] = useState<{ tone: 'info' | 'warn' | 'danger'; text: string } | null>(null);
  const tenantId = me?.active_tenant_id ?? null;

  const school = useQuery({
    queryKey: ['school-display', schoolId],
    queryFn: () => get<SchoolDetail>(`/schools/${schoolId}`),
  });

  const tenant = useQuery({
    queryKey: ['display-logo'],
    queryFn: () => get<{ logo: string | null }>('/display/logo'),
  });

  useEffect(() => {
    if (!school.data) return;
    setPrincipal(school.data.principal_name ?? '');
    setDeputy(school.data.deputy_name ?? '');
  }, [school.data]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['school-display', schoolId] });
    void queryClient.invalidateQueries({ queryKey: ['schools'] });
  };

  const saveNames = useMutation({
    mutationFn: () =>
      patch(`/schools/${schoolId}`, { principal_name: principal.trim(), deputy_name: deputy.trim() }),
    onSuccess: () => {
      setNote({ tone: 'info', text: 'حُفظت الأسماء وتظهر على الشاشة خلال ثوانٍ.' });
      refresh();
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const makeLink = useMutation({
    mutationFn: () => post<{ url: string }>(`/schools/${schoolId}/display-link`),
    onSuccess: (res) => {
      setNote({ tone: 'info', text: `الرابط جاهز: ${res.url}` });
      refresh();
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const stopLink = useMutation({
    mutationFn: () => del(`/schools/${schoolId}/display-link`),
    onSuccess: () => {
      setNote({ tone: 'warn', text: 'أُوقف الرابط؛ الشاشات المفتوحة عليه ستتوقف.' });
      refresh();
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const saveLogo = useMutation({
    mutationFn: (logo: string | null) => put('/display/logo', { logo }),
    onSuccess: () => {
      setNote({ tone: 'info', text: 'حُفظ الشعار لكل شاشات المديرية.' });
      void queryClient.invalidateQueries({ queryKey: ['display-logo'] });
    },
    onError: (e: Error) => setNote({ tone: 'danger', text: e.message }),
  });

  const onLogo = (file: File): void => {
    if (file.size > MAX_LOGO_BYTES) {
      setNote({ tone: 'danger', text: 'حجم الشعار أكبر من 300 ك.ب. اختر صورة أصغر (PNG أو SVG).' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => saveLogo.mutate(String(reader.result));
    reader.onerror = () => setNote({ tone: 'danger', text: 'تعذّرت قراءة الصورة.' });
    reader.readAsDataURL(file);
  };

  const token = school.data?.display_token ?? null;
  const url = token ? `${window.location.origin}/display/${token}` : null;
  const logo = tenant.data?.logo ?? null;

  return (
    <Modal
      title={`شاشة مدرسة ${schoolName}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>إغلاق</button>
          <button className="primary" disabled={saveNames.isPending} onClick={() => saveNames.mutate()}>
            {saveNames.isPending ? 'جارٍ الحفظ…' : 'حفظ الأسماء'}
          </button>
        </>
      }
    >
      {note ? <Notice tone={note.tone}>{note.text}</Notice> : null}

      {school.isLoading ? (
        <Loading />
      ) : (
        <>
          <p style={{ color: 'var(--text-soft)', marginTop: 0, fontSize: 13.5 }}>
            شاشة تُعرض في ساحة المدرسة أو الممر: اسم المدرسة والمديرية، المدير والمعاون، التاريخ، وجدول
            اليوم مع إبراز الحصة الجارية وعدّاد انتهائها. تُفتح بالرابط دون تسجيل دخول ولا تحوي أي تحكم.
          </p>

          <div className="grid cols-2">
            <Field label="مدير المدرسة">
              <input value={principal} onChange={(e) => setPrincipal(e.target.value)} maxLength={160} />
            </Field>
            <Field label="معاون المدير">
              <input value={deputy} onChange={(e) => setDeputy(e.target.value)} maxLength={160} />
            </Field>
          </div>

          <Field label="رابط الشاشة" hint="افتحه في متصفح الشاشة بوضع ملء الشاشة (F11)">
            {url ? (
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input value={url} readOnly className="mono" style={{ flex: '1 1 260px' }} />
                <button
                  onClick={() => {
                    void navigator.clipboard?.writeText(url);
                    setNote({ tone: 'info', text: 'نُسخ الرابط.' });
                  }}
                >
                  نسخ
                </button>
                <a href={url} target="_blank" rel="noreferrer">
                  فتح
                </a>
              </div>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>لا يوجد رابط بعد.</div>
            )}
          </Field>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => makeLink.mutate()} disabled={makeLink.isPending}>
              {token ? 'إنشاء رابط جديد (يُبطل القديم)' : 'إنشاء رابط الشاشة'}
            </button>
            {token ? (
              <button className="danger" onClick={() => stopLink.mutate()} disabled={stopLink.isPending}>
                إيقاف الرابط
              </button>
            ) : null}
          </div>

          <Field
            label="شعار الوزارة"
            hint="صورة PNG أو SVG حتى 300 ك.ب، تظهر أعلى كل شاشات المديرية"
          >
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              {logo ? (
                <img
                  src={logo}
                  alt="الشعار الحالي"
                  style={{ width: 56, height: 56, objectFit: 'contain', background: 'var(--surface-2)', borderRadius: 8 }}
                />
              ) : (
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>لا يوجد شعار؛ تُعرض علامة افتراضية.</span>
              )}
              <input
                type="file"
                accept="image/png,image/svg+xml,image/jpeg,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onLogo(file);
                }}
              />
              {logo ? (
                <button className="ghost" onClick={() => saveLogo.mutate(null)} disabled={saveLogo.isPending}>
                  إزالة الشعار
                </button>
              ) : null}
            </div>
          </Field>
        </>
      )}
    </Modal>
  );
}
